import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FileDown, Play, Table2 } from "lucide-react";

const GROUPS = [
  { label: "Payroll", types: ["payroll_register", "payroll_summary", "employee_earnings_history", "ytd_summary"] },
  { label: "Codes & Costs", types: ["earnings_by_code", "deductions_by_code", "employer_cost"] },
  { label: "Time & Leave", types: ["vacation_leave", "hours"] },
  { label: "Compliance & Accounting", types: ["statutory_deductions", "cra_remittance", "gl_posting", "payroll_reconciliation", "payroll_exceptions"] },
  { label: "Year-End", types: ["t4_reconciliation", "roe_history"] },
];

const fmt = (v) => (typeof v === "number" ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v === true ? "Yes" : v === false ? "No" : (v ?? ""));

export default function ReportingWorkspace({ companyId }) {
  const [catalog, setCatalog] = useState([]);
  const [rtype, setRtype] = useState("payroll_register");
  const [year, setYear] = useState(new Date().getFullYear());
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/reporting/${companyId}/catalog`).then((r) => setCatalog(r.data.reports)).catch(() => {});
    api.get(`/employees?company_id=${companyId}`).then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
  }, [companyId]);

  const label = catalog.find((c) => c.type === rtype)?.name || rtype;
  const q = `year=${year}${employeeId ? `&employee_id=${employeeId}` : ""}`;
  const run = useCallback(async () => {
    if (!companyId) return;
    setBusy(true);
    try { const r = await api.get(`/reporting/${companyId}/${rtype}?${q}`); setData(r.data); }
    catch (e) { toast.error("Could not run report"); setData(null); }
    finally { setBusy(false); }
  }, [companyId, rtype, q]);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const exportUrl = (ext) => `${backend}/api/reporting/${companyId}/${rtype}.${ext}?${q}`;

  if (!companyId) return null;
  return (
    <div data-testid="reporting-workspace" className="border border-black bg-white mb-8">
      <div className="px-5 py-4 border-b border-black/10 flex items-center gap-2">
        <Table2 className="h-4 w-4" /><span className="font-bold tracking-tight">Reports</span>
        <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground ml-2">Accounting-grade · authoritative</span>
      </div>
      <div className="px-5 py-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Report</span>
          <select data-testid="report-type-select" value={rtype} onChange={(e) => { setRtype(e.target.value); setData(null); }} className="mt-1 min-w-[16rem] border border-input bg-white px-3 py-2 text-sm">
            {GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.types.map((t) => <option key={t} value={t}>{catalog.find((c) => c.type === t)?.name || t}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Tax year</span>
          <input data-testid="report-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 w-28 border border-input bg-white px-3 py-2 text-sm" /></label>
        <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Employee</span>
          <select data-testid="report-employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="mt-1 min-w-[12rem] border border-input bg-white px-3 py-2 text-sm">
            <option value="">All employees</option>
            {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
          </select>
        </label>
        <button data-testid="run-report-button" onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 bg-black text-white px-4 py-2 text-sm hover:bg-neutral-800 disabled:opacity-40"><Play className="h-4 w-4" />Run Report</button>
        {data && <>
          <a data-testid="export-csv" href={exportUrl("csv")} className="inline-flex items-center gap-1.5 border border-black px-3 py-2 text-sm hover:bg-black hover:text-white"><FileDown className="h-4 w-4" />CSV</a>
          <a data-testid="export-xlsx" href={exportUrl("xlsx")} className="inline-flex items-center gap-1.5 border border-black px-3 py-2 text-sm hover:bg-black hover:text-white"><FileDown className="h-4 w-4" />XLSX</a>
        </>}
      </div>

      {data && (
        <div className="px-5 pb-5">
          <div className="text-[11px] font-mono text-muted-foreground mb-2">{data.provenance?.report} · {data.provenance?.basis} · {data.rows.length} row(s)</div>
          <div className="overflow-x-auto border border-border">
            <table data-testid="report-table" className="w-full text-sm">
              <thead className="bg-neutral-50 sticky top-0"><tr>{data.columns.map((c) => <th key={c} className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2 whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}</tr></thead>
              <tbody>
                {data.rows.slice(0, 300).map((r, i) => (
                  <tr key={i} className="border-t border-border hover:bg-neutral-50">
                    {data.columns.map((c) => <td key={c} className={`px-3 py-1.5 tabular-nums whitespace-nowrap ${typeof r[c] === "number" && r[c] < 0 ? "text-red-600" : ""}`}>{fmt(r[c])}</td>)}
                  </tr>
                ))}
              </tbody>
              {data.totals && (
                <tfoot><tr className="border-t-2 border-black bg-neutral-50 font-semibold">
                  {data.columns.map((c, i) => <td key={c} className="px-3 py-2 tabular-nums whitespace-nowrap">{i === 0 ? "TOTALS" : (data.totals[c] !== undefined ? fmt(data.totals[c]) : "")}</td>)}
                </tr></tfoot>
              )}
            </table>
          </div>
          {data.totals && (
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground tabular-nums">
              {Object.entries(data.totals).map(([k, v]) => <span key={k} data-testid={`total-${k}`}><span className="font-mono uppercase tracking-widest">{k.replace(/_/g, " ")}:</span> {fmt(v)}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
